import type { SolcellerFormData } from '../types';

/** Matches PvlibApi `constants.ROOF_TILES_*` and `calculate_pdc0_watts` (panel path). */
const ROOF_TILES_EFFICIENCY_FACTOR = 0.8;
const ROOF_TILES_AREA_FACTOR = 0.75;

const efficiencyByInstallationYear = (year: number): number => {
  if (year < 2018) {
    return 0.177;
  }
  if (year <= 2019) {
    return 0.18;
  }
  if (year === 2020) {
    return 0.2;
  }
  if (year <= 2023) {
    return 0.218;
  }
  if (year <= 2025) {
    return 0.223;
  }
  return 0.244;
};

const pdc0KwFromSqmPanel = (
  panel: NonNullable<SolcellerFormData['sqmPanels']>[number],
  installationYear: number
): number => {
  const efficiency = efficiencyByInstallationYear(installationYear);

  if (panel.panelType === 'solcellepanel') {
    const area = panel.area;
    if (area == null || area <= 0) {
      return 0;
    }
    return area * efficiency;
  }

  if (panel.panelType !== 'solcelletakstein') {
    return 0;
  }

  if (panel.hasActivePanelArea === 'yes') {
    const area = panel.area;
    if (area == null || area <= 0) {
      return 0;
    }
    return area * efficiency * ROOF_TILES_EFFICIENCY_FACTOR;
  }

  if (panel.hasActivePanelArea === 'no') {
    const roofArea = panel.roofArea;
    if (roofArea == null || roofArea <= 0 || panel.azimuth == null) {
      return 0;
    }
    const base = roofArea * efficiency * ROOF_TILES_EFFICIENCY_FACTOR;
    const azimuth = panel.azimuth;
    if (azimuth >= 45 && azimuth <= 315) {
      return base * ROOF_TILES_AREA_FACTOR;
    }
    return 0;
  }

  return 0;
};

/**
 * Estimated installed DC capacity (kWp) from form state — mirrors backend PV logic for validation caps.
 */
export const estimateTotalInstalledKwp = (data: SolcellerFormData): number => {
  const year = data.installationYear;
  if (year == null || Number.isNaN(year)) {
    return 0;
  }

  if (data.hasCapacityData === 'yes' && data.kwpPanels?.length) {
    return data.kwpPanels.reduce((sum, p) => sum + (p.effect && p.effect > 0 ? p.effect : 0), 0);
  }

  if (data.hasCapacityData === 'no' && data.sqmPanels?.length) {
    return data.sqmPanels.reduce((sum, p) => sum + pdc0KwFromSqmPanel(p, year), 0);
  }

  return 0;
};
